import { useState, useTransition } from 'react';
import { ErrorMessage } from '#components/atoms/forms/index.ts';
import { ConfirmDialog } from '#components/patterns/confirmations/confirm-dialog/ConfirmDialog.tsx';
import type { EndpointOIDC } from '#dn-types/endpoint-oidc.ts';
import { useMoveActiveProviderMutation } from './MoveDomainDialog.queries.tsx';

type Props = {
  domainName: string;
  fromEndpointOIDC: EndpointOIDC;
  toEndpointOIDC: EndpointOIDC;
  onCancel: () => void;
  onSuccess: () => void;
};

export function MoveDomainDialog({ domainName, fromEndpointOIDC, toEndpointOIDC, onCancel, onSuccess }: Props) {
  const [isPending, startTransition] = useTransition();
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const moveProviderMutations = useMoveActiveProviderMutation({ onError: setErrorMsg, onSuccess });

  return (
    <ConfirmDialog
      title={<div className="truncate">Switch providers for {domainName}?</div>}
      acceptButtonText="Switch provider"
      onCancel={onCancel}
      onAccept={() => {
        startTransition(async () => {
          await moveProviderMutations(fromEndpointOIDC, toEndpointOIDC);
        });
      }}
      disableAccept={isPending}
      shouldRestoreFocusViaFocusScope={true}
    >
      <p className="p">
        You’re switching providers for users with a {domainName} email address. The current provider (
        {fromEndpointOIDC.name}) will be disabled, and all new logins will use {toEndpointOIDC.name}. Existing sessions
        will not be invalidated.
      </p>
      <p className="p mb-0">Are you sure you want to continue?</p>

      {errorMsg ?
        <ErrorMessage className="mt-24">{errorMsg}</ErrorMessage>
      : null}
    </ConfirmDialog>
  );
}
